import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { Venue, VenueGroup } from '@/types';

const venueSchema = z.object({
  name: z.string().min(1, 'Venue name is required'),
  slug: z.string().min(1, 'URL slug is required').regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  description: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  country: z.string().default('UK'),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  groupId: z.string().optional().or(z.literal('')),
  isActive: z.boolean().default(true),
});

type VenueFormData = z.infer<typeof venueSchema>;

export default function VenueForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const queryClient = useQueryClient();

  const { data: venue, isLoading: venueLoading } = useQuery<Venue>({
    queryKey: ['venue', id],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${id}`);
      return response.data;
    },
    enabled: isEditing,
  });

  const { data: groups } = useQuery<VenueGroup[]>({
    queryKey: ['groups-all'],
    queryFn: async () => {
      const response = await api.get('/admin/groups/all');
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<VenueFormData>({
    resolver: zodResolver(venueSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      address: '',
      city: '',
      postcode: '',
      country: 'UK',
      phone: '',
      email: '',
      website: '',
      groupId: '',
      isActive: true,
    },
  });

  const name = watch('name');

  // Auto-generate slug from name
  useEffect(() => {
    if (!isEditing && name) {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setValue('slug', slug);
    }
  }, [name, isEditing, setValue]);

  useEffect(() => {
    if (venue) {
      const v = venue as unknown as Record<string, unknown>;
      reset({
        name: v.name as string,
        slug: v.slug as string,
        description: (v.description as string) || '',
        address: (v.address as string) || '',
        city: (v.city as string) || '',
        postcode: (v.postcode as string) || '',
        country: (v.country as string) || 'UK',
        phone: (v.phone as string) || '',
        email: (v.email as string) || '',
        website: (v.website as string) || '',
        groupId: (v.group_id as string) || '',
        isActive: (v.is_active as boolean) ?? true,
      });
    }
  }, [venue, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: VenueFormData) => {
      const payload = {
        ...data,
        groupId: data.groupId || undefined,
        email: data.email || undefined,
        website: data.website || undefined,
      };
      const response = await api.post('/admin/venues', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      navigate('/venues');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: VenueFormData) => {
      const payload = {
        ...data,
        groupId: data.groupId || undefined,
        email: data.email || undefined,
        website: data.website || undefined,
      };
      await api.put(`/admin/venues/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      queryClient.invalidateQueries({ queryKey: ['venue', id] });
      navigate('/venues');
    },
  });

  const onSubmit = async (data: VenueFormData) => {
    if (isEditing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  if (isEditing && venueLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/venues')}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Venue' : 'Create Venue'}
        </h1>
      </div>

      <div className="card max-w-2xl">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {(createMutation.error || updateMutation.error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {(createMutation.error as Error)?.message || (updateMutation.error as Error)?.message || 'An error occurred'}
            </div>
          )}

          <div className="space-y-1">
            <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
            <p className="text-sm text-gray-500">Core details about the venue</p>
          </div>

          <div>
            <label htmlFor="name" className="label">
              Venue Name
            </label>
            <input
              id="name"
              type="text"
              className="input"
              placeholder="e.g. The Grand Hotel"
              {...register('name')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="slug" className="label">
              URL Slug
            </label>
            <div className="flex items-center">
              <span className="text-gray-500 text-sm mr-2">booking.appyfeed.com/</span>
              <input
                id="slug"
                type="text"
                className="input"
                placeholder="the-grand-hotel"
                {...register('slug')}
              />
            </div>
            {errors.slug && (
              <p className="mt-1 text-sm text-red-600">{errors.slug.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="label">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              className="input"
              placeholder="A brief description of the venue..."
              {...register('description')}
            />
          </div>

          <div>
            <label htmlFor="groupId" className="label">
              Venue Group (Optional)
            </label>
            <select id="groupId" className="input" {...register('groupId')}>
              <option value="">No group</option>
              {groups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Assign to a group if this venue is part of a chain
            </p>
          </div>

          <hr className="my-6" />

          <div className="space-y-1">
            <h2 className="text-lg font-medium text-gray-900">Location</h2>
            <p className="text-sm text-gray-500">Where the venue is located</p>
          </div>

          <div>
            <label htmlFor="address" className="label">
              Street Address
            </label>
            <input
              id="address"
              type="text"
              className="input"
              placeholder="123 High Street"
              {...register('address')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="city" className="label">
                City
              </label>
              <input
                id="city"
                type="text"
                className="input"
                placeholder="London"
                {...register('city')}
              />
            </div>
            <div>
              <label htmlFor="postcode" className="label">
                Postcode
              </label>
              <input
                id="postcode"
                type="text"
                className="input"
                placeholder="SW1A 1AA"
                {...register('postcode')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="country" className="label">
              Country
            </label>
            <select id="country" className="input" {...register('country')}>
              <option value="UK">United Kingdom</option>
              <option value="IE">Ireland</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
            </select>
          </div>

          <hr className="my-6" />

          <div className="space-y-1">
            <h2 className="text-lg font-medium text-gray-900">Contact Details</h2>
            <p className="text-sm text-gray-500">How to reach the venue</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="label">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                className="input"
                placeholder="+44 20 1234 5678"
                {...register('phone')}
              />
            </div>
            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="bookings@venue.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="website" className="label">
              Website
            </label>
            <input
              id="website"
              type="url"
              className="input"
              placeholder="https://www.venue.com"
              {...register('website')}
            />
            {errors.website && (
              <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
            )}
          </div>

          {isEditing && (
            <>
              <hr className="my-6" />
              <div className="flex items-center gap-3">
                <input
                  id="isActive"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  {...register('isActive')}
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Venue is active and accepting bookings
                </label>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/venues')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Venue' : 'Create Venue'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
